import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "../api";

/* ═════════════════════════════════════════════════════════════
   주소 검색 컴포넌트 — 네이버 Geocoding API 기반
   Juso.go.kr 스타일 UI, 사이트 전체에서 공용 사용

   Props:
     value       - 현재 주소 문자열
     onSelect    - ({ address, roadAddress, jibunAddress, lat, lng, postalCode }) => void
     onChange     - (addressText) => void  — 텍스트만 필요할 때
     placeholder - 입력 필드 placeholder
     label       - 레이블 (optional)
     required    - required 속성
     className   - wrapper 추가 클래스
     inputClassName - input 추가 클래스
     showCoords  - 좌표 표시 여부 (기본 false)
     adminOnly   - true이면 /api/admin/naver/geocode 사용, false이면 /api/address/search 사용
     compact     - 작은 사이즈 모드
     modalMode   - true이면 팝업 모달로 검색 (기본 false, 인라인 드롭다운)
   ═════════════════════════════════════════════════════════════ */

export default function AddressSearch({
  value = "",
  onSelect,
  onChange,
  placeholder = "도로명 / 지번 주소 검색",
  label,
  required,
  className = "",
  inputClassName = "",
  showCoords = false,
  adminOnly = false,
  compact = false,
  modalMode = false,
  useDaumDirect = false,
}) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selected, setSelected] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalQuery, setModalQuery] = useState("");
  const [modalResults, setModalResults] = useState([]);
  const [modalSearching, setModalSearching] = useState(false);
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const pick = (addr) => {
    const display = addr.roadAddress || addr.jibunAddress || addr.address || "";
    setQuery(display);
    setShowDropdown(false);
    setShowModal(false);
    setSelected(true);
    onSelect?.({
      address: display,
      roadAddress: addr.roadAddress || "",
      jibunAddress: addr.jibunAddress || "",
      lat: parseFloat(addr.lat) || 0,
      lng: parseFloat(addr.lng) || 0,
      postalCode: addr.postalCode || "",
    });
    onChange?.(display);
  };

  // ── Daum Postcode popup (primary method for partial road names) ──
  const openDaumPostcode = () => {
    if (!window.daum?.Postcode) return;
    new window.daum.Postcode({
      oncomplete: async (data) => {
        const road = data.roadAddress || data.address || "";
        const jibun = data.jibunAddress || "";
        const zonecode = data.zonecode || "";
        // Try to get coordinates from backend
        let lat = 0, lng = 0;
        try {
          const endpoint = adminOnly ? "/api/admin/naver/geocode" : "/api/address/search";
          const r = await api.get(endpoint, { params: { query: road } });
          if (r.data.ok && r.data.addresses?.length > 0) {
            lat = parseFloat(r.data.addresses[0].lat) || 0;
            lng = parseFloat(r.data.addresses[0].lng) || 0;
          }
        } catch { /* coordinates optional */ }
        pick({ roadAddress: road, jibunAddress: jibun, lat, lng, postalCode: zonecode });
      },
      width: "100%",
      height: "100%",
    }).open();
  };

  const searchAddress = useCallback(async (q, isModal = false) => {
    if (!q || q.length < 2) {
      if (isModal) setModalResults([]);
      else setResults([]);
      return;
    }
    if (isModal) setModalSearching(true);
    else setSearching(true);

    try {
      const endpoint = adminOnly ? "/api/admin/naver/geocode" : "/api/address/search";
      const r = await api.get(endpoint, { params: { query: q } });
      const addresses = r.data.ok ? (r.data.addresses || []) : [];
      if (isModal) setModalResults(addresses);
      else { setResults(addresses); if (addresses.length > 0) setShowDropdown(true); }
    } catch { /* ignore */ }
    finally {
      if (isModal) setModalSearching(false);
      else setSearching(false);
    }
  }, [adminOnly]);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setSelected(false);
    onChange?.(v);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => searchAddress(v), 400);
  };

  const openModal = () => {
    setModalQuery(query);
    setModalResults([]);
    setShowModal(true);
    if (query.length >= 2) searchAddress(query, true);
  };

  const handleModalSearch = () => {
    if (modalQuery.length >= 2) searchAddress(modalQuery, true);
  };

  const sz = compact ? "text-xs py-1.5 px-2.5" : "text-sm py-2.5 px-3.5";
  const baseInput = `w-full rounded-xl border border-gray-200 bg-gray-50 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition ${sz}`;

  // ── Daum Postcode direct mode ──
  if (useDaumDirect) {
    return (
      <div className={`relative ${className}`}>
        {label && <label className="block text-xs font-medium text-gray-500 mb-1.5">{label}</label>}
        <div className="relative">
          <input
            type="text" value={query} readOnly
            onClick={openDaumPostcode}
            placeholder={placeholder}
            required={required}
            className={`${baseInput} cursor-pointer pr-10 ${inputClassName}`}
          />
          <button type="button" onClick={openDaumPostcode}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
          </button>
          {selected && <span className="absolute right-12 top-1/2 -translate-y-1/2 text-green-500 text-xs">✓</span>}
        </div>
      </div>
    );
  }

  // ── Modal mode ──
  if (modalMode) {
    return (
      <div className={`relative ${className}`}>
        {label && <label className="block text-xs font-medium text-gray-500 mb-1.5">{label}</label>}
        <div className="relative">
          <input
            type="text" value={query} readOnly
            onClick={openModal}
            placeholder={placeholder}
            required={required}
            className={`${baseInput} cursor-pointer pr-10 ${inputClassName}`}
          />
          <button type="button" onClick={openModal}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
          </button>
          {selected && <span className="absolute right-12 top-1/2 -translate-y-1/2 text-green-500 text-xs">✓</span>}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center pt-[10vh]"
            onClick={() => setShowModal(false)}>
            <div className="bg-[#1a2744] rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden"
              onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="relative p-6 pb-4">
                <button onClick={() => setShowModal(false)}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center transition">✕</button>
                <div className="flex items-center gap-2 bg-white rounded-full px-4 py-2.5 shadow-lg">
                  <input
                    type="text"
                    value={modalQuery}
                    onChange={(e) => setModalQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleModalSearch()}
                    placeholder="도로명 / 지번 주소 검색"
                    autoFocus
                    className="flex-1 text-sm outline-none bg-transparent text-gray-800 placeholder-gray-400"
                  />
                  {modalQuery && (
                    <button onClick={() => { setModalQuery(""); setModalResults([]); }}
                      className="text-gray-400 hover:text-gray-600 text-sm">✕</button>
                  )}
                  <button onClick={handleModalSearch}
                    className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                  </button>
                </div>
              </div>

              {/* Results */}
              <div className="bg-white rounded-t-2xl min-h-[300px] max-h-[60vh] overflow-auto">
                {modalSearching ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="animate-spin w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full"/>
                  </div>
                ) : modalResults.length > 0 ? (
                  <div className="p-4 space-y-2">
                    <p className="text-sm text-gray-500 mb-3 text-center">
                      검색어 "<strong className="text-blue-600">{modalQuery}</strong>" 에 대한 결과 총 <strong>{modalResults.length}</strong>건
                    </p>
                    {modalResults.map((addr, i) => (
                      <button key={i} type="button" onClick={() => pick(addr)}
                        className="w-full text-left bg-gray-50 hover:bg-blue-50 rounded-xl p-4 transition border border-gray-100 hover:border-blue-200">
                        <div className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-200 text-gray-500 text-xs font-medium flex items-center justify-center mt-0.5">{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium">도로명주소</span>
                              <span className="text-sm font-medium text-gray-800 truncate">{addr.roadAddress || "-"}</span>
                            </div>
                            {addr.jibunAddress && (
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-medium">지번</span>
                                <span className="text-xs text-gray-500">{addr.jibunAddress}</span>
                              </div>
                            )}
                            {showCoords && (
                              <p className="text-[10px] text-blue-400 mt-1">📍 {addr.lat}, {addr.lng}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : modalQuery.length >= 2 && !modalSearching ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400 gap-3">
                    <p className="text-sm">검색 결과가 없습니다</p>
                    <button type="button" onClick={() => { setShowModal(false); openDaumPostcode(); }}
                      className="px-4 py-2 bg-yellow-500 text-white text-sm rounded-lg hover:bg-yellow-600 transition">
                      주소검색으로 찾기
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-2">
                    <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                    <p className="text-sm">도로명 또는 지번 주소를 입력하세요</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Inline dropdown mode ──
  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {label && <label className="block text-xs font-medium text-gray-500 mb-1.5">{label}</label>}
      <div className="flex gap-1.5">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            type="text" value={query} onChange={handleChange}
            placeholder={placeholder}
            required={required}
            className={`${baseInput} pr-8 ${inputClassName}`}
          />
          {searching && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs animate-spin">⏳</span>}
          {!searching && selected && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-xs">✓</span>}
        </div>
        <button type="button" onClick={openDaumPostcode}
          className={`flex-shrink-0 rounded-xl bg-yellow-500 hover:bg-yellow-600 text-white font-medium transition whitespace-nowrap ${compact ? "text-xs px-2.5 py-1.5" : "text-xs px-3 py-2"}`}>
          주소검색
        </button>
      </div>
      {showDropdown && results.length > 0 && (
        <div className="absolute z-40 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-auto">
          {results.map((addr, i) => (
            <button key={i} type="button" onClick={() => pick(addr)}
              className="w-full text-left px-3 py-2.5 hover:bg-blue-50 transition border-b border-gray-50 last:border-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">도로명</span>
                <p className="text-xs font-medium text-gray-800 truncate">{addr.roadAddress || addr.jibunAddress}</p>
              </div>
              {addr.roadAddress && addr.jibunAddress && (
                <div className="flex items-center gap-2">
                  <span className="text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium">지번</span>
                  <p className="text-[10px] text-gray-400">{addr.jibunAddress}</p>
                </div>
              )}
              {addr.placeName && (
                <p className="text-[10px] text-purple-500 mt-0.5">🏢 {addr.placeName}</p>
              )}
              {showCoords && <p className="text-[10px] text-blue-500 mt-0.5">📍 {addr.lat}, {addr.lng}</p>}
            </button>
          ))}
        </div>
      )}
      {showDropdown && results.length === 0 && !searching && query.length >= 2 && !selected && (
        <div className="absolute z-40 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-3 text-center text-xs text-gray-400">
          결과가 없습니다. <button type="button" onClick={openDaumPostcode} className="text-blue-500 underline">주소검색</button> 버튼을 눌러 검색해 보세요.
        </div>
      )}
    </div>
  );
}
